import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { HandHeart, Heart } from "@phosphor-icons/react";
import { useRazorpay } from "react-razorpay";
import api, { formatApiError } from "@/lib/api";

const AMOUNTS = { INR: [500, 1000, 2500, 5000], USD: [25, 50, 100, 250] };
const SYMBOL = { INR: "₹", USD: "$" };
const INTERESTS = ["Teaching", "Fundraising", "Field Work", "Content & Media", "Tech Support"];

const inputCls =
  "w-full bg-bone border border-forest/25 px-4 py-3 text-sm text-ink placeholder:text-moss/50 focus:outline-none focus:border-terra focus:ring-1 focus:ring-terra transition";

const VolunteerForm = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", interest: "Teaching", message: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/volunteers", form);
      toast.success("Welcome aboard! Our volunteer team will reach out soon.");
      setForm({ name: "", email: "", phone: "", interest: "Teaching", message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="volunteer-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <input required placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="volunteer-name-input" />
        <input required type="email" placeholder="Email address" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} data-testid="volunteer-email-input" />
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <input placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} data-testid="volunteer-phone-input" />
        <select value={form.interest} onChange={(e) => setForm({ ...form, interest: e.target.value })} className={inputCls} data-testid="volunteer-interest-select">
          {INTERESTS.map((i) => <option key={i} value={i}>{i}</option>)}
        </select>
      </div>
      <textarea placeholder="Tell us why you'd like to volunteer (optional)" rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className={inputCls} data-testid="volunteer-message-input" />
      <button type="submit" disabled={busy} data-testid="volunteer-submit-btn"
        className="bg-forest text-bone font-bold px-8 py-4 hover:-translate-y-0.5 hover:shadow-lg transition-all duration-200 disabled:opacity-60 disabled:translate-y-0">
        {busy ? "Signing you up…" : "Join as a Volunteer"}
      </button>
    </form>
  );
};

const DonateCard = () => {
  const [currency, setCurrency] = useState("INR");
  const [amount, setAmount] = useState(AMOUNTS.INR[1]);
  const [custom, setCustom] = useState("");
  const [frequency, setFrequency] = useState("one-time");
  const [form, setForm] = useState({ name: "", email: "", pan: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const { Razorpay } = useRazorpay();

  const sym = SYMBOL[currency];
  const finalAmount = custom ? Number(custom) : amount;

  const payload = () => ({
    name: form.name,
    email: form.email,
    pan: form.pan.trim().toUpperCase() || undefined,
    phone: form.phone.trim() || undefined,
    origin_url: window.location.origin,
  });

  const switchCurrency = (c) => {
    setCurrency(c);
    setAmount(AMOUNTS[c][1]);
    setCustom("");
  };

  const openRazorpay = async () => {
    const { data } = await api.post("/payments/razorpay/order", { ...payload(), amount: finalAmount });
    const rzp = new Razorpay({
      key: data.key_id,
      amount: data.amount_paise,
      currency: "INR",
      order_id: data.order_id,
      name: "Learning Lights Initiative",
      description: "One-time donation",
      prefill: { name: form.name, email: form.email, contact: form.phone.trim() || undefined },
      theme: { color: "#0B3D84" },
      handler: async (resp) => {
        try {
          await api.post("/payments/razorpay/verify", {
            order_id: resp.razorpay_order_id,
            payment_id: resp.razorpay_payment_id,
            signature: resp.razorpay_signature,
          });
          window.location.href = `/payment/success?razorpay_order_id=${resp.razorpay_order_id}`;
        } catch {
          toast.error("We couldn't confirm your payment. If money was deducted, it will reflect shortly.");
          setBusy(false);
        }
      },
      modal: { ondismiss: () => setBusy(false) },
    });
    rzp.on("payment.failed", () => {
      toast.error("Payment failed or was cancelled. No money was deducted.");
      setBusy(false);
    });
    rzp.open();
  };

  const openRazorpaySubscription = async () => {
    const { data } = await api.post("/payments/razorpay/subscription", { ...payload(), amount: finalAmount });
    const rzp = new Razorpay({
      key: data.key_id,
      subscription_id: data.subscription_id,
      name: "Learning Lights Initiative",
      description: `Monthly donation — ₹${finalAmount.toLocaleString("en-IN")}/month`,
      prefill: { name: form.name, email: form.email, contact: form.phone.trim() || undefined },
      theme: { color: "#0B3D84" },
      handler: async (resp) => {
        try {
          await api.post("/payments/razorpay/subscription/verify", {
            subscription_id: resp.razorpay_subscription_id,
            payment_id: resp.razorpay_payment_id,
            signature: resp.razorpay_signature,
          });
          window.location.href = `/payment/success?razorpay_subscription_id=${resp.razorpay_subscription_id}`;
        } catch {
          toast.error("We couldn't confirm your subscription. If money was deducted, it will reflect shortly.");
          setBusy(false);
        }
      },
      modal: { ondismiss: () => setBusy(false) },
    });
    rzp.on("payment.failed", () => {
      toast.error("Payment failed or was cancelled. No money was deducted.");
      setBusy(false);
    });
    rzp.open();
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!finalAmount || finalAmount <= 0) {
      toast.error("Please choose or enter an amount.");
      return;
    }
    if (form.pan && !/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(form.pan.trim().toUpperCase())) {
      toast.error("PAN format looks incorrect — e.g. ABCDE1234F (or leave it blank).");
      return;
    }
    setBusy(true);
    try {
      if (frequency === "monthly") {
        if (currency === "INR") {
          await openRazorpaySubscription();
        } else {
          await api.post("/donations", { ...form, amount: finalAmount, frequency, currency });
          toast.success(`Thank you, ${form.name.split(" ")[0]}! Your ${sym}${finalAmount}/month pledge is recorded — our team will set up your monthly giving personally.`);
          setForm({ name: "", email: "" });
          setCustom("");
          setBusy(false);
        }
      } else if (currency === "INR") {
        await openRazorpay();
      } else {
        const { data } = await api.post("/payments/checkout", {
          ...payload(),
          amount: finalAmount,
        });
        window.location.href = data.checkout_url;
      }
    } catch (err) {
      toast.error(formatApiError(err));
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-forest text-bone p-8 lg:p-10 space-y-5" data-testid="donate-form">
      <div className="flex gap-2" data-testid="donate-currency-toggle">
        {[["INR", "₹ Pay in INR"], ["USD", "$ Pay in USD"]].map(([c, label]) => (
          <button key={c} type="button" onClick={() => switchCurrency(c)} data-testid={`donate-currency-${c.toLowerCase()}`}
            className={`flex-1 py-2.5 text-sm font-bold border transition-colors duration-200 ${currency === c ? "bg-bone text-forest border-bone" : "border-bone/30 text-bone/70 hover:border-bone/60"}`}>
            {label}
          </button>
        ))}
      </div>
      <div className="flex gap-2" data-testid="donate-frequency-toggle">
        {["one-time", "monthly"].map((f) => (
          <button key={f} type="button" onClick={() => setFrequency(f)} data-testid={`donate-frequency-${f}`}
            className={`flex-1 py-2.5 text-sm font-bold border transition-colors duration-200 ${frequency === f ? "bg-terra border-terra text-bone" : "border-bone/30 text-bone/70 hover:border-bone/60"}`}>
            {f === "one-time" ? "One-time" : "Monthly"}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-4 gap-2" data-testid="donate-amounts">
        {AMOUNTS[currency].map((a) => (
          <button key={a} type="button" onClick={() => { setAmount(a); setCustom(""); }} data-testid={`donate-amount-${a}`}
            className={`py-3 font-serif font-bold text-lg border transition-colors duration-200 ${!custom && amount === a ? "bg-terra border-terra text-bone" : "border-bone/30 text-bone/80 hover:border-bone/60"}`}>
            {sym}{a.toLocaleString("en-IN")}
          </button>
        ))}
      </div>
      <input type="number" min="1" placeholder={`Custom amount (${currency})`} value={custom} onChange={(e) => setCustom(e.target.value)}
        className="w-full bg-transparent border border-bone/30 px-4 py-3 text-sm placeholder:text-bone/40 focus:outline-none focus:border-terra transition"
        data-testid="donate-custom-amount-input" />
      <div className="grid sm:grid-cols-2 gap-3">
        <input required placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
          className="w-full bg-transparent border border-bone/30 px-4 py-3 text-sm placeholder:text-bone/40 focus:outline-none focus:border-terra transition"
          data-testid="donate-name-input" />
        <input required type="email" placeholder="Email address" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
          className="w-full bg-transparent border border-bone/30 px-4 py-3 text-sm placeholder:text-bone/40 focus:outline-none focus:border-terra transition"
          data-testid="donate-email-input" />
      </div>
      {currency === "INR" && (
        <div className="grid sm:grid-cols-2 gap-3">
          <input type="tel" placeholder="Phone (optional)" value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            className="w-full bg-transparent border border-bone/30 px-4 py-3 text-sm placeholder:text-bone/40 focus:outline-none focus:border-terra transition"
            data-testid="donate-phone-input" />
          <input placeholder="PAN (optional, for 80G)" value={form.pan}
            onChange={(e) => setForm({ ...form, pan: e.target.value.toUpperCase().slice(0, 10) })}
            className="w-full bg-transparent border border-bone/30 px-4 py-3 text-sm placeholder:text-bone/40 focus:outline-none focus:border-terra transition tracking-widest uppercase"
            data-testid="donate-pan-input" />
        </div>
      )}
      <button type="submit" disabled={busy} data-testid="donate-submit-btn"
        className="w-full bg-terra text-bone font-bold py-4 hover:-translate-y-0.5 hover:shadow-xl transition-all duration-200 disabled:opacity-60 disabled:translate-y-0">
        {busy ? "Opening secure checkout…" : frequency === "monthly"
          ? (currency === "INR" ? `Give ${sym}${(finalAmount || 0).toLocaleString("en-IN")} / month` : `Pledge ${sym}${(finalAmount || 0).toLocaleString("en-IN")} / month`)
          : `Donate ${sym}${(finalAmount || 0).toLocaleString("en-IN")} securely`}
      </button>
      <p className="text-xs text-bone/50 leading-relaxed" data-testid="donate-mock-note">
        {frequency === "monthly"
          ? (currency === "INR"
            ? "A secure Razorpay window will open — your card or UPI is charged automatically each month. Your 80G tax receipt arrives by email after the first charge."
            : "Monthly giving in USD is set up personally by our team — we'll reach out within 48 hours to activate your recurring gift.")
          : currency === "INR"
            ? "A secure Razorpay window will open — pay by UPI, card, or netbanking. Your 80G tax receipt arrives by email right after payment."
            : "You'll be redirected to Stripe's secure checkout to complete your one-time gift by card. We never see or store your card details."}
      </p>
    </form>
  );
};

export const GetInvolved = () => (
  <section id="get-involved" className="py-24 lg:py-32 bg-bonealt/60 border-t border-forest/15" data-testid="get-involved-section">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.7 }}
        className="mb-16"
      >
        <span className="text-xs font-bold tracking-[0.3em] uppercase text-terra">Get Involved</span>
        <h2 className="font-serif font-bold tracking-tighter text-forest text-4xl sm:text-5xl lg:text-6xl mt-4 max-w-3xl" data-testid="get-involved-heading">
          Two ways to <span className="italic text-terra">light a lamp</span> today.
        </h2>
      </motion.div>

      <div className="grid lg:grid-cols-2 gap-10 items-start">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7 }}
        >
          <div className="flex items-center gap-3 mb-5">
            <HandHeart size={28} weight="duotone" className="text-terra" />
            <h3 className="font-serif font-bold text-2xl text-forest tracking-tight">Give your time</h3>
          </div>
          <p className="text-base text-moss leading-relaxed mb-6 max-w-md">
            Teach a class, ride along with a mobile library, or lend your professional
            skills. Two hours a week changes a child's year.
          </p>
          <VolunteerForm />
          <div className="mt-8 relative">
            <div className="absolute -inset-0 translate-x-3 translate-y-3 bg-terra/80 -z-0" />
            <img
              src="/photos/teacher-group.jpg"
              alt="LLI volunteer teachers at a training session"
              className="relative w-full h-56 object-cover img-warm border border-forest/20"
              data-testid="volunteer-image"
            />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, delay: 0.12 }}
        >
          <div className="flex items-center gap-3 mb-5">
            <Heart size={28} weight="duotone" className="text-terra" />
            <h3 className="font-serif font-bold text-2xl text-forest tracking-tight">Give a gift</h3>
          </div>
          <p className="text-base text-moss leading-relaxed mb-6 max-w-md">
            Together, we can light the path to learning — one child, one book, one opportunity at a time.
          </p>
          <DonateCard />
        </motion.div>
      </div>
    </div>
  </section>
);
